import './ThinkingMessage.scss';
import { useState, useEffect, useCallback } from 'react';

const BrainIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round">
    <path d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z" />
    <path d="M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z" />
    <path d="M15 13a4.5 4.5 0 0 1-3-4 4.5 4.5 0 0 1-3 4" />
    <path d="M17.599 6.5a3 3 0 0 0 .399-1.375" />
    <path d="M6.003 5.125A3 3 0 0 0 6.401 6.5" />
    <path d="M3.477 10.896a4 4 0 0 1 .585-.396" />
    <path d="M19.938 10.5a4 4 0 0 1 .585.396" />
    <path d="M6 18a4 4 0 0 1-1.967-.516" />
    <path d="M19.967 17.484A4 4 0 0 1 18 18" />
  </svg>
);

const SpinnerIcon = () => (
  <div className="thinking-spinner">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <circle
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeDasharray="50 15"
        className="spinner-circle"
      />
    </svg>
  </div>
);

const ThinkingMessage = ({ progress }) => {
  const [elapsedTime, setElapsedTime] = useState(0);
  const [messageHistory, setMessageHistory] = useState([]);

  const getStageMessage = useCallback(() => {
    const defaultMessages = {
      input_analysis: 'Understanding your security query...',
      function_calls: 'Querying security databases...',
      response_generation: 'Generating comprehensive analysis...',
      complete: 'Analysis complete',
    };

    if (progress?.stage_details?.description) {
      return progress.stage_details.description;
    }

    return defaultMessages[progress?.current_stage] || 'Analyzing your request...';
  }, [progress]);

  // Timer effect
  useEffect(() => {
    const timer = setInterval(() => {
      setElapsedTime(prev => prev + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Track message history
  useEffect(() => {
    const currentMessage = getStageMessage();

    setMessageHistory(prev => {
      // Check if this message already exists
      if (prev.length === 0 || prev[prev.length - 1] !== currentMessage) {
        return [...prev, currentMessage];
      }
      return prev;
    });
  }, [getStageMessage]);

  const formatTime = seconds => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (!progress && messageHistory.length === 0) {
    return (
      <div className="thinking-container">
        <div className="thinking-header">
          <div className="thinking-title">
            <BrainIcon />
            <span>Thoughts and actions</span>
          </div>
          <div className="thinking-timer">{formatTime(elapsedTime)}</div>
        </div>
        <div className="thinking-content">
          <div className="thinking-stage">
            <SpinnerIcon />
            <span>Planning</span>
          </div>
          <ul className="thinking-messages">
            <li className="thinking-message-item current">
              <span>Analyzing your request...</span>
            </li>
          </ul>
        </div>
      </div>
    );
  }

  return (
    <div className="thinking-container">
      <div className="thinking-header">
        <div className="thinking-title">
          <BrainIcon />
          <span>Thoughts and actions</span>
        </div>
        <div className="thinking-timer">{formatTime(elapsedTime)}</div>
      </div>
      <div className="thinking-content">
        <div className="thinking-stage">
          <SpinnerIcon />
          <span>Planning</span>
        </div>
        <ul className="thinking-messages">
          {messageHistory.map((message, index) => (
            <li
              key={index}
              className={`thinking-message-item ${index === messageHistory.length - 1 ? 'current' : 'previous'}`}>
              <span>{message}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default ThinkingMessage;
